/**
 * Validation utilities for merchant details forms
 */

export const validateStatementDescriptor = (value: string): string => {
  if (!value || value.trim() === '') {
    return '' // Empty is allowed (optional field)
  }

  const trimmedValue = value.trim()

  // Check minimum length
  if (trimmedValue.length < 5) {
    return 'Statement descriptor must be at least 5 characters long'
  }

  // Check maximum length
  if (trimmedValue.length > 22) {
    return 'Statement descriptor must be 22 characters or fewer'
  }

  // Check for at least one letter
  if (!/[a-zA-Z]/.test(trimmedValue)) {
    return 'Statement descriptor must contain at least one letter'
  }

  // Check for forbidden characters: <>"'\\*
  if (/[<>"'\\*]/.test(trimmedValue)) {
    return 'Statement descriptor cannot contain < > " \' \\ or * characters'
  }

  // Check that only allowed characters are used
  // Allowed: letters, numbers, spaces, and specific punctuation: .,:;!?-_&()/
  if (!/^[a-zA-Z0-9\s.,:;!?\-_&()/]+$/.test(trimmedValue)) {
    return 'Statement descriptor can only contain letters, numbers, spaces, and these punctuation marks: . , : ; ! ? - _ & ( ) /'
  }

  return '' // Valid
}

export const validateName = (value: string): string => {
  if (!value || value.trim() === '') {
    return 'Business name is required'
  }

  const trimmedValue = value.trim()

  if (trimmedValue.length < 2) {
    return 'Business name must be at least 2 characters long'
  }

  if (trimmedValue.length > 100) {
    return 'Business name must be 100 characters or fewer'
  }

  // Check for forbidden characters that might cause issues
  if (/[<>"'\\]/.test(trimmedValue)) {
    return 'Business name cannot contain < > " \' or \\ characters'
  }

  return '' // Valid
}

export const validateEmail = (value: string): string => {
  if (!value || value.trim() === '') {
    return 'Email address is required'
  }

  const trimmedValue = value.trim()

  // Basic email regex validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (!emailRegex.test(trimmedValue)) {
    return 'Please enter a valid email address'
  }

  if (trimmedValue.length > 254) {
    return 'Email address is too long'
  }

  return '' // Valid
}

export const validateWebsite = (value: string): string => {
  if (!value || value.trim() === '') {
    return '' // Website is optional
  }

  const trimmedValue = value.trim()

  // Website URL validation regex (matches the API schema)
  const websiteRegex =
    /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[\w.-]*)*(\?[\w=&.-]*)?$/
  if (!websiteRegex.test(trimmedValue)) {
    return 'Please provide a valid website. If providing a social media handle, please share the full link to the profile.'
  }

  return '' // Valid
}

export const validateTelephone = (value: string): string => {
  if (!value || value.trim() === '') {
    return '' // Telephone is optional
  }

  const trimmedValue = value.trim()

  // Phone number validation regex (matches the API schema)
  const phoneRegex = /^\+[1-9]\d{1,14}$/
  if (!phoneRegex.test(trimmedValue)) {
    return 'Phone number must start with + followed by country code (e.g., +1234567890)'
  }

  return '' // Valid
}

export const validateAllFields = (formData: {
  name: string
  email: string
  website: string
  telephone: string
  statementDescriptor: string
}) => {
  return {
    name: validateName(formData.name),
    email: validateEmail(formData.email),
    website: validateWebsite(formData.website),
    telephone: validateTelephone(formData.telephone),
    statementDescriptor: validateStatementDescriptor(
      formData.statementDescriptor,
    ),
  }
}

export const hasValidationErrors = (
  errors: Record<string, string>,
): boolean => {
  return Object.values(errors).some((error) => error !== '')
}
